import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChatWindow } from "./types";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import {
  IconArrowLeft,
  LoadingSpinner,
  PrimaryBlueButton,
  Severity
} from "@velaro/velaro-component-lib";
import TabBar from "./Tabs/TabBar";
import Appearance from "./Tabs/Appearance/Appearance";
import Widgets from "./Tabs/Widgets/Widgets";
import Channels from "./Tabs/Channels/Channels";
import PreChatSurvey from "./Tabs/PreChatSurvey";
import PostChatSurvey from "./Tabs/PostChatSurvey";
import Settings from "./Tabs/Settings/Settings";
import { LanguageConfigModel, StyleConfigModel } from "../types";
import Edit from "@/kb/articles/Edit";
import EditableLabel from "./EditableLabel";

const defaultChatWindow: ChatWindow = {
  id: 0,
  siteId: 0,
  name: "",
  updateTimestamp: "",
  createdByUserName: "",
  styleConfig: {} as StyleConfigModel,
  languageConfig: {} as LanguageConfigModel
};

const tabs = [
  "Appearance",
  "Widgets",
  "Channels"
  // "Pre-chat Survey",
  // "Post-chat Survey",
  // "Settings"
];

export default function ChatWindowEditor() {
  const { chatWindowId: stringChatWindowId } = useParams<{
    chatWindowId: string;
  }>();
  const [chatWindow, setChatWindow] = useState<ChatWindow>(defaultChatWindow);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const [workingChatWindow, setWorkingChatWindow] =
    useState<ChatWindow>(defaultChatWindow);
  const [saving, setSaving] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [stagedLogoFile, setStagedLogoFile] = React.useState<File | null>(null);
  const [stagedGraphicFile, setStagedGraphicFile] = React.useState<File | null>(null);
  const [stagedHeaderBgFile, setStagedHeaderBgFile] = React.useState<File | null>(null);
  const [stagedLauncherIconFile, setStagedLauncherIconFile] = React.useState<File | null>(null);
  const [stagedContentWidgetImageFile, setStagedContentWidgetImageFile] = React.useState<File | null>(null);

  const api = useApi();
  const toastAlert = useToastAlert();
  const navigate = useNavigate();

  const isDirty =
    JSON.stringify(workingChatWindow) !== JSON.stringify(chatWindow) ||
    stagedLogoFile !== null ||
    stagedGraphicFile !== null ||
    stagedHeaderBgFile !== null ||
    stagedLauncherIconFile !== null ||
    stagedContentWidgetImageFile !== null;

  useEffect(() => {
    if (!stringChatWindowId) {
      console.error("Chat window ID is required");
      return;
    }

    async function fetchChatWindow() {
      setLoading(true);
      const chatWindowId = Number(stringChatWindowId);
      if (isNaN(chatWindowId)) {
        toastAlert.displayToast(Severity.Error, "Invalid chat window ID.");
        setLoading(false);
        return;
      }
      const response = await api.messaging.get(
        `ChatWindow/Get/${chatWindowId}`
      );
      if (!response.ok) {
        const errorText = await response.text();
        toastAlert.displayToast(Severity.Error, errorText);
        setLoading(false);
        return;
      }
      const chatWindow = (await response.json()) as ChatWindow;
      chatWindow.styleConfig = chatWindow.styleConfig || ({} as StyleConfigModel);
      chatWindow.languageConfig = chatWindow.languageConfig || ({} as LanguageConfigModel);
      setChatWindow(chatWindow);
      setWorkingChatWindow(chatWindow);
      setLoading(false);
    }
    fetchChatWindow();
  }, [api.messaging, stringChatWindowId, toastAlert]);

  async function saveChanges() {
    setSaving(true);
    try {
      const formData = new FormData();

      // Build styleConfig for the request without mutating React state.
      // When a file is staged, clear the URL from the payload so the server
      // uses the uploaded file instead of the old base64/URL value.
      const styleConfigPayload = { ...workingChatWindow.styleConfig };
      if (stagedLogoFile) {
        formData.append("logo", stagedLogoFile);
        styleConfigPayload.logoUrl = undefined;
      }
      if (stagedGraphicFile) {
        formData.append("queueGraphic", stagedGraphicFile);
        styleConfigPayload.queueGraphicUrl = undefined;
      }
      if (stagedHeaderBgFile) {
        formData.append("headerBgImage", stagedHeaderBgFile);
        styleConfigPayload.headerBackgroundImageUrl = undefined;
      }
      if (stagedLauncherIconFile) {
        formData.append("launcherIcon", stagedLauncherIconFile);
        styleConfigPayload.launcherIconUrl = undefined;
      }
      if (stagedContentWidgetImageFile) {
        formData.append("contentWidgetImage", stagedContentWidgetImageFile);
        styleConfigPayload.contentWidgetImageUrl = undefined;
      }
      formData.append("name", workingChatWindow.name);
      formData.append("styleConfig", JSON.stringify(styleConfigPayload));
      formData.append("languageConfig", JSON.stringify(workingChatWindow.languageConfig));

      const response = await api.messaging.postFormData(
        `ChatWindow/Update/${workingChatWindow.id}`,
        formData
      );

      if (!response.ok) {
        const errorText = await response.text();
        toastAlert.displayToast(Severity.Error, errorText);
        return;
      }

      toastAlert.displayToast(Severity.Success, "Changes saved");

      const responseChatWindow = (await response.json()) as ChatWindow;
      responseChatWindow.styleConfig = responseChatWindow.styleConfig || ({} as StyleConfigModel);
      responseChatWindow.languageConfig = responseChatWindow.languageConfig || ({} as LanguageConfigModel);
      setChatWindow(responseChatWindow);
      setWorkingChatWindow(responseChatWindow);
      setStagedLogoFile(null);
      setStagedGraphicFile(null);
      setStagedHeaderBgFile(null);
      setStagedLauncherIconFile(null);
      setStagedContentWidgetImageFile(null);
    } catch (ex: any) {
      toastAlert.displayToast(Severity.Error, "Failed to save changes.");
      console.error("saveChanges failed:", ex?.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div
        className="sticky top-0 z-10 bg-slate-50 -mx-6 px-6 pt-4 pb-2 relative"
      >
        <div className="absolute left-0 right-0 -top-3 sm:-top-6 h-3 sm:h-6 bg-slate-50" aria-hidden="true" />
        <div className="flex justify-between">
          <div className="flex items-center">
            <IconArrowLeft
              className="text-slate-500 hover:cursor-pointer"
              onClick={() => {
                if (isDirty) {
                  setShowUnsavedDialog(true);
                } else {
                  navigate(-1);
                }
              }}
            />
            <EditableLabel
              value={workingChatWindow.name}
              onChange={(newValue) =>
                setWorkingChatWindow({ ...workingChatWindow, name: newValue })
              }
            />
          </div>
          <div className="flex items-center gap-3">
            {isDirty && (
              <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>
            )}
            <PrimaryBlueButton
              label={saving ? "Saving…" : "Save"}
              onClick={saveChanges}
            />
          </div>
        </div>
        <TabBar
          tabs={tabs}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />
      </div>
      <div className="p-4 mt-6">
        {selectedTab === "Appearance" && (
          <Appearance
            workingChatWindow={workingChatWindow}
            setWorkingChatWindow={setWorkingChatWindow}
            setStagedLogoFile={setStagedLogoFile}
            setStagedGraphicFile={setStagedGraphicFile}
            setStagedHeaderBgFile={setStagedHeaderBgFile}
          />
        )}
        {selectedTab === "Widgets" && (
          <Widgets
            workingChatWindow={workingChatWindow}
            setWorkingChatWindow={setWorkingChatWindow}
            setStagedContentWidgetImageFile={setStagedContentWidgetImageFile}
          />
        )}
        {selectedTab === "Channels" && (
          <Channels
            workingChatWindow={workingChatWindow}
            setWorkingChatWindow={setWorkingChatWindow}
          />
        )}
        {/* {selectedTab === "Pre-chat Survey" && <PreChatSurvey />}
      {selectedTab === "Post-chat Survey" && <PostChatSurvey />}
      {selectedTab === "Settings" && <Settings />} */}
      </div>

      {showUnsavedDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-96 max-w-full mx-4">
            <div className="text-base font-semibold mb-2">Unsaved changes</div>
            <p className="text-sm text-slate-600 mb-5">
              You have unsaved changes. Do you want to save before leaving?
            </p>
            <div className="flex gap-2 justify-end">
              <button
                className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50"
                onClick={() => setShowUnsavedDialog(false)}
              >
                Keep editing
              </button>
              <button
                className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50 text-red-600"
                onClick={() => { setShowUnsavedDialog(false); navigate(-1); }}
              >
                Discard & leave
              </button>
              <button
                className="px-4 py-2 text-sm rounded-lg bg-primary text-white hover:opacity-90"
                onClick={async () => { await saveChanges(); setShowUnsavedDialog(false); navigate(-1); }}
              >
                Save & leave
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
